import React, { ComponentType, Suspense } from 'react'
import ReactDOM from 'react-dom'
import { isNullOrUndefined } from 'is-what'
import 'global-styles.scss'

import withProvider from 'redux/with-provider'
import User from 'components/user/User'
import PlayerSearchBar from 'components/search/PlayerSearchBar'
import PlayerFilterBar from 'components/search/PlayerFilterBar'
import SearchResults from 'components/search/SearchResults'
import H2HModal from 'components/head-to-head/H2HModal'
import ProfileRouter from 'components/profile/ProfileRouter/ProfileRouter'
import VsPlayerSearchBar from 'components/search/VsPlayerSearchBar'
import MyAccount from 'components/account/MyAccount'
import WatchListsRouter from 'components/watchlist/WatchListsRouter'
import FastTrackPlayerDetails from 'components/registration/fastTrackRegistration'
import Register from 'components/registration/Register'
import Psq from 'components/psq/Psq'
import PrivacyPolicy from 'components/privacy-policy/PrivacyPolicy'
import TermsAndConditions from 'components/privacy-policy/TermsAndConditions'
import TournamentChart from 'components/chart/TournamentChart/TournamentChart'
import ResultsSummarySection from 'components/chart/ResultsDoughnutChart/ResultsSummarySection'
import HistoryLineChart from 'components/chart/HistoryLineChart/HistoryLineChart'
import MessageScreen from 'components/screens/UserMessageFullScreen'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import UserMessageFullScreen from 'components/screens/UserMessageFullScreen'
import { MessageTypes } from 'constants/message-screens'
import withAppBoot from 'components/generic/hoc/withAppBoot'
import SearchBarPlaceholder from 'components/search/SearchBarPlaceholder'
import InfoBox from 'components/generic/InfoBox'
import i18n from 'constants/strings'
import NavbarControls from 'components/navbar/NavbarControls'
import SignInOrAccountMenuMobile from 'components/navbar/AccountMobileMenu'
import { WithCustomHTMLAttribute } from 'types/global'
import MatchResultListings from 'components/profile/competitions/MatchResultListings'
import PsqPublic from 'components/psq/PsqPublic'
import PostAScore from 'components/navbar/PostAScore'
import { RegisterBtn } from 'components/navbar/buttons'

type PropType = 'string' | 'number' | 'object' | 'array' | 'boolean'
type FilterBarProps = React.ComponentProps<typeof PlayerFilterBar>
type WithPlayerIdProps = WithCustomHTMLAttribute<'playerId'>
type HistoryLineChartProps = React.ComponentProps<typeof HistoryLineChart>
type MessageScreenProps = React.ComponentProps<typeof MessageScreen>

const LoadingScreen = () => <LoadingSpinnerWithContainer fullScreen />
const ErrorScreen = ({ error }: { error: Error }) => (
  <UserMessageFullScreen messageType={MessageTypes.APP_ERROR} error={error} />
)

interface Widget {
  widget: ComponentType<any>
  id: string
  params?: {
    attributeName: string
    reactName: string
    reactType: PropType
  }[]
  bootPlaceholder?: ComponentType
  bootErrorFallback?: ComponentType<{ error: Error }>
}

const getParams = <T extends {}>(
  attributeName: string,
  reactName: keyof T,
  reactType: PropType
) => ({
  attributeName,
  reactName: reactName as string,
  reactType,
})

const searchIdParam = {
  attributeName: 'search_id',
  reactName: 'searchId',
  reactType: 'string' as PropType,
}

const redirectOnViewAll = {
  attributeName: 'redirectOnViewAll',
  reactName: 'redirectOnViewAll',
  reactType: 'boolean' as PropType,
}

const enableSelfSubmission = {
  attributeName: 'enable-self-submission',
  reactName: 'enableSelfSubmission',
  reactType: 'boolean' as PropType,
}

const hideResultsForAnonUser = {
  attributeName: 'hide-match-results',
  reactName: 'hideResultsForAnonUser',
  reactType: 'boolean' as PropType,
}

const widgets: Widget[] = [
  {
    widget: User,
    id: 'user-controller',
    params: [enableSelfSubmission],
  },
  {
    widget: NavbarControls,
    id: 'navbar-controls',
  },
  {
    widget: SignInOrAccountMenuMobile,
    id: 'menu-account',
  },
  {
    widget: PlayerSearchBar,
    id: 'player-search',
    params: [
      redirectOnViewAll,
      searchIdParam,
      getParams('no_suggestions_extra_info', 'noSuggestionsExtraInfo', 'string'),
      getParams('search_on_load', 'shouldSearchOnLoad', 'boolean'),
      getParams('remove_sorting_on_search', 'removeSortingOnSearch', 'boolean'),
    ],
    bootPlaceholder: () => <SearchBarPlaceholder loading />,
    bootErrorFallback: () => <SearchBarPlaceholder error />,
  },
  {
    widget: PlayerFilterBar,
    id: 'player-filter-bar',
    params: [
      getParams<FilterBarProps>('min_range', 'minRange', 'number'),
      getParams<FilterBarProps>('max_range', 'maxRange', 'number'),
      getParams<FilterBarProps>('step_size', 'stepSize', 'number'),
      getParams('filters_on_mount', 'applyFiltersOnMount', 'boolean'),
      searchIdParam,
    ],
    bootPlaceholder: () => null,
    bootErrorFallback: () => null,
  },
  {
    widget: ProfileRouter,
    id: 'player-profile',
    params: [enableSelfSubmission, hideResultsForAnonUser],
    bootPlaceholder: LoadingScreen,
    bootErrorFallback: ErrorScreen,
  },
  {
    widget: TournamentChart,
    id: 'chart-player-tournament-results',
    params: [getParams<WithPlayerIdProps>('player_id', 'playerId', 'string')],
  },
  {
    widget: ResultsSummarySection,
    id: 'results-summary-doughnut',
    params: [getParams<WithPlayerIdProps>('player_id', 'playerId', 'string')],
  },
  {
    widget: HistoryLineChart,
    id: 'wtn-history-chart',
    params: [getParams<HistoryLineChartProps>('player_ids', 'playerIds', 'array')],
  },
  {
    widget: SearchResults,
    id: 'search-results',
    params: [searchIdParam],
    bootPlaceholder: () => <LoadingSpinnerWithContainer containerHeight={400} />,
    bootErrorFallback: () => (
      <div style={{ maxWidth: '600px', margin: '20px auto', padding: '0 20px' }}>
        <InfoBox iconColor='red' boxColor='red'>
          <h3 style={{ marginBottom: '10px' }}>{i18n.t('strMessageScreen.appError')}</h3>
          <p style={{ marginBottom: '10px' }}>{i18n.t('strMessageScreen.appErrorInfo')}</p>
          <a href={`${window.location.href}`} style={{ textDecoration: 'underline' }}>
            {i18n.t('strMessageScreen.appErrorLink')}
          </a>
        </InfoBox>
      </div>
    ),
  },
  { widget: H2HModal, id: 'head-to-head-modal' },
  { widget: VsPlayerSearchBar, id: 'vs-player-search' },
  { widget: MatchResultListings, id: 'match-result-listings' },
  {
    widget: MyAccount,
    id: 'edit-profile',
    params: [enableSelfSubmission],
    bootPlaceholder: LoadingScreen,
    bootErrorFallback: ErrorScreen,
  },
  {
    widget: WatchListsRouter,
    id: 'watchlists',
    params: [getParams<WithPlayerIdProps>('player_id', 'playerId', 'string')],
    bootPlaceholder: LoadingScreen,
    bootErrorFallback: ErrorScreen,
  },
  {
    widget: FastTrackPlayerDetails,
    id: 'fast-track-player-details',
    bootPlaceholder: LoadingScreen,
    bootErrorFallback: ErrorScreen,
  },
  {
    widget: Register,
    id: 'walk-up-registration',
    bootPlaceholder: LoadingScreen,
    bootErrorFallback: ErrorScreen,
  },
  { widget: Psq, id: 'psq', bootPlaceholder: LoadingScreen, bootErrorFallback: ErrorScreen },
  {
    widget: PsqPublic,
    id: 'psq-public',
    params: [getParams('registration-url', 'registrationUrl', 'string')],
    bootPlaceholder: LoadingScreen,
    bootErrorFallback: ErrorScreen,
  },
  { widget: PrivacyPolicy, id: 'privacy-policy' },
  { widget: TermsAndConditions, id: 'terms-and-conditions' },
  {
    widget: MessageScreen,
    id: 'message-screen',
    params: [getParams<MessageScreenProps>('message_type', 'messageType', 'string')],
  },
  {
    widget: RegisterBtn,
    id: 'sign-up-or-subscribe',
  },
  {
    widget: PostAScore,
    id: 'post-a-score-desktop',
    params: [enableSelfSubmission],
  },
  {
    widget: PostAScore,
    id: 'post-a-score-mobile',
    params: [enableSelfSubmission],
  },
]

export const registerWidgets = () => {
  widgets.forEach(({ id, widget, params, bootPlaceholder, bootErrorFallback }) => {
    // Each component requires it's own redux provider since
    // they are mounted independently into the DOM.
    const mountTo = document.getElementById(id)

    if (bootPlaceholder) widget = withAppBoot(widget, bootPlaceholder, bootErrorFallback)

    // Get props from html/div attributes
    const props =
      mountTo && params
        ? params.reduce(
            (acc, p) => ({
              ...acc,
              [p.reactName]: convertType({
                ...p,
                val: mountTo.getAttribute(p.attributeName) || undefined,
              }),
            }),
            {}
          )
        : {}

    const Widget = withProvider(widget)
    mountTo &&
      ReactDOM.render(
        <Suspense fallback={null}>
          <Widget {...props} />
        </Suspense>,
        mountTo
      )
  })
}

const convertType = ({
  attributeName,
  reactType,
  val,
}: {
  attributeName: string
  reactType: PropType
  val: string | undefined
}) => {
  if (reactType === 'string' || isNullOrUndefined(val)) {
    return val
  }
  try {
    return JSON.parse(val)
  } catch (e) {
    console.error(`attribute '${attributeName}' with value '${val}' not in json format:'`, e)
    return undefined
  }
}
